import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useRemove911Address, use911Address } from '../hooks';
import { formatPhoneNumber } from '@/lib/utils';
import { TriangleAlert } from 'lucide-react';

interface RemoveE911ModalProps {
  tn: string | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal to confirm removal of E911 registration from a DID.
 * This removes the 911 address, NOT the DID itself.
 */
export function RemoveE911Modal({ tn, isOpen, onClose }: RemoveE911ModalProps) {
  const remove911Address = useRemove911Address();
  const { data: address } = use911Address(tn || '');
  const [confirmText, setConfirmText] = useState('');

  // Reset confirm text when modal closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
    }
  }, [isOpen]);

  if (!isOpen || !tn) return null;

  const handleRemove = () => {
    remove911Address.mutate(tn, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const canConfirmRemove = confirmText === 'REMOVE';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!remove911Address.isPending) {
          onClose();
        }
      }}
      title="Remove E911 Registration"
      size="md"
    >
      <div className="space-y-4">
        {/* Warning */}
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <TriangleAlert className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">
              This will remove the E911 registration
            </p>
            <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
              <li>Emergency services will no longer have location information</li>
              <li>All 911 email alerts will also be removed</li>
              <li>The DID will remain active but without E911</li>
            </ul>
          </div>
        </div>

        {/* DID Info */}
        <div className="p-4 bg-slate-50 rounded-lg">
          <p className="text-sm text-slate-600">Phone number:</p>
          <p className="text-xl font-bold text-slate-900 font-mono mt-1">
            {formatPhoneNumber(tn)}
          </p>
        </div>

        {/* Current Address Display */}
        {address?.address && (
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600 font-medium">Current E911 address:</p>
            <p className="text-sm text-slate-800 mt-1">
              {address.address.address1}
              {address.address.address2 && `, ${address.address.address2}`}
              <br />
              {address.address.city}, {address.address.state} {address.address.zip_code}
            </p>
          </div>
        )}

        {/* Confirmation Input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Type <span className="font-bold text-red-600">REMOVE</span> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
            placeholder="REMOVE"
            disabled={remove911Address.isPending}
          />
        </div>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={remove911Address.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemove}
            disabled={!canConfirmRemove}
            isLoading={remove911Address.isPending}
          >
            Remove E911 Registration
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}
